import PropTypes from 'prop-types';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import Circle from 'ol/style/Circle';
import Image from 'ol/style/Image';

defaultStyle.propTypes = {
  /** If set to true, features with property 'active' will be rendered with activeColor. */
  useActiveStyle: PropTypes.bool,
  /** If set to true, features with property 'hover' will be rendered with hoverColor. */
  useHoverStyle: PropTypes.bool,
  /** Image for feature. */
  image: PropTypes.instanceOf(Image),
  /** Size of circle for point features. Ignored if image is set. */
  pointRadius: PropTypes.number,
  /** Fill color of rendered geometry. Ignored for point features if image is set. */
  fillColor: PropTypes.string,
  /** Stroke color of rendered geometry. Ignored for point features if image is set. */
  strokeColor: PropTypes.string,
  /** Stroke width of rendered geometry. Ignored for point features if image is set. */
  strokeWidth: PropTypes.string,
  /** Fill/stroke color of active features. */
  activeColor: PropTypes.string,
  /** Fill/stroke color of hovered features. */
  hoverColor: PropTypes.string,
  /** Line dash pattern for stroke. */
  lineDash: PropTypes.arrayOf(PropTypes.number),
  /** Line dash offset for stroke. */
  lineDashOffset: PropTypes.number,
  /**
   * Function providing text style for feature.
   *
   * @param {Feature} feature Feature to style<br/>
   * @param {number} resolution Map resolution
   * @returns {ol/style/Text}
   */
  labelText: PropTypes.func
};

defaultStyle.defaultProps = {
  useActiveStyle: false,
  useHoverStyle: false,
  pointRadius: 10,
  activeColor: 'rgb(255,200,0)',
  hoverColor: 'rgb(255,0,255)',
  fillColor: 'rgb(0,150,200)',
  strokeColor: 'rgb(255,255,255)',
  strokeWidth: 0
};

/**
 * Renders features in a default configurable style.
 *
 * @component
 */
export default function defaultStyle(_ref) {
  var _ref$useActiveStyle = _ref.useActiveStyle,
      useActiveStyle = _ref$useActiveStyle === undefined ? false : _ref$useActiveStyle,
      _ref$useHoverStyle = _ref.useHoverStyle,
      useHoverStyle = _ref$useHoverStyle === undefined ? false : _ref$useHoverStyle,
      image = _ref.image,
      _ref$pointRadius = _ref.pointRadius,
      pointRadius = _ref$pointRadius === undefined ? 10 : _ref$pointRadius,
      _ref$activeColor = _ref.activeColor,
      activeColor = _ref$activeColor === undefined ? 'rgb(255,200,0)' : _ref$activeColor,
      _ref$hoverColor = _ref.hoverColor,
      hoverColor = _ref$hoverColor === undefined ? 'rgb(255,0,255)' : _ref$hoverColor,
      _ref$fillColor = _ref.fillColor,
      fillColor = _ref$fillColor === undefined ? 'rgb(0,150,200)' : _ref$fillColor,
      _ref$strokeColor = _ref.strokeColor,
      strokeColor = _ref$strokeColor === undefined ? 'rgb(255,255,255)' : _ref$strokeColor,
      _ref$strokeWidth = _ref.strokeWidth,
      strokeWidth = _ref$strokeWidth === undefined ? 0 : _ref$strokeWidth,
      lineDash = _ref.lineDash,
      lineDashOffset = _ref.lineDashOffset,
      labelText = _ref.labelText;

  return function (feature, resolution) {
    var geometryType = feature.getGeometry().getType();
    var hover = feature.get('hover') && !!useHoverStyle;
    var active = feature.get('active') && !!useActiveStyle;

    var fill = new Fill({
      color: active ? activeColor : hover ? hoverColor : fillColor
    });
    var stroke = new Stroke({
      width: strokeWidth,
      lineDash: lineDash,
      lineDashOffset: lineDashOffset,
      color: active ? activeColor : hover ? hoverColor : strokeColor
    });
    var zIndex = active ? 10 : hover ? 9 : 1;
    var text = labelText && labelText(feature, resolution);

    switch (geometryType) {
      case 'Point':
      case 'MultiPoint':
        return new Style({
          image: image || new Circle({
            radius: pointRadius,
            fill: fill,
            stroke: stroke
          }),

          text: text,
          zIndex: zIndex
        });

      default:
        return new Style({
          fill: fill,
          stroke: stroke,
          text: text,
          zIndex: zIndex
        });
    }
  };
}